// [[wikilink|alias]] inline rule with three render modes.
//
//   'inert'      — emits a token AND installs a renderer rule that
//                  produces <span class="wikilink-inert">{display}</span>.
//                  Used by the share-page renderer (recipients can't
//                  navigate workspace-relative targets) and any future
//                  surface that wants to show wikilinks without making
//                  them clickable.
//
//   'extraction' — emits a token but leaves it un-rendered (empty string).
//                  Used by packages/core/src/index/link-extract.ts which
//                  walks the token stream to populate the links table at
//                  write time. The token shape carries
//                  meta.target + meta.anchor for the visitor.
//
//   'plain'      — installs nothing; `[[foo]]` passes through as
//                  literal text. Used by the SPA reader (current
//                  behavior — toggling this on is a future decision).
//
// Token shape (modes 'inert' and 'extraction'):
//
//   token.type    = 'wikilink'
//   token.markup  = '[[]]'
//   token.content = display    // alias if pipe present, else target
//   token.meta    = { target, anchor: anchor | null }

import type MarkdownIt from 'markdown-it';

export interface WikilinkPluginOptions {
  mode?: 'inert' | 'extraction' | 'plain';
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function wikilinkPlugin(md: MarkdownIt, opts?: WikilinkPluginOptions): void {
  const mode = opts?.mode ?? 'inert';
  if (mode === 'plain') return;

  md.inline.ruler.before('link', 'wikilink', (state, silent) => {
    if (state.src.charCodeAt(state.pos) !== 0x5b /* [ */) return false;
    if (state.src.charCodeAt(state.pos + 1) !== 0x5b) return false;

    const end = state.src.indexOf(']]', state.pos + 2);
    if (end < 0) return false;
    const newline = state.src.indexOf('\n', state.pos + 2);
    if (newline >= 0 && newline < end) return false;

    if (!silent) {
      const inner = state.src.slice(state.pos + 2, end);
      if (inner.length > 0) {
        const pipe = inner.indexOf('|');
        const targetWithAnchor = pipe < 0 ? inner : inner.slice(0, pipe);
        const display = pipe < 0 ? targetWithAnchor : inner.slice(pipe + 1);
        const hash = targetWithAnchor.indexOf('#');
        const target = hash < 0 ? targetWithAnchor : targetWithAnchor.slice(0, hash);
        const anchor = hash < 0 ? null : targetWithAnchor.slice(hash);

        const tok = state.push('wikilink', '', 0);
        tok.markup = '[[]]';
        tok.content = display;
        tok.meta = { target, anchor };
      }
    }
    state.pos = end + 2;
    return true;
  });

  if (mode === 'inert') {
    md.renderer.rules.wikilink = (tokens, idx) => {
      const tk = tokens[idx]!;
      return `<span class="wikilink-inert">${escapeHtml(tk.content)}</span>`;
    };
  } else {
    // mode === 'extraction'. Caller walks tokens directly via md.parse();
    // if md.render() is ever called, emit empty for the wikilink token
    // so we don't leak markdown-it's renderToken default (which would
    // emit '<>' for the tag-less token).
    md.renderer.rules.wikilink = () => '';
  }
}
